'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { ArrowRight, CheckCircle2 } from 'lucide-react'

export function Hero() {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden bg-encore-dark">
      {/* Background Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-encore-primary/20 rounded-full blur-[100px]" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-encore-accent/10 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-encore-primary/10 border border-encore-primary/20 text-encore-accent-light text-xs font-medium mb-6 animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-encore-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-encore-accent"></span>
            </span>
            New: AI Feedback Manager Available
          </div>
          
          <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6 leading-tight animate-slide-up">
            The Operating System for <span className="encore-gradient-text">Hospitality</span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Unify your data, automate feedback, and grow revenue with AI. 
            The all-in-one platform built for modern restaurant owners.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <Button size="lg" asChild className="w-full sm:w-auto bg-encore-gradient hover:opacity-90 text-lg h-12 px-8">
              <Link href="/register">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto border-gray-700 text-gray-300 hover:text-white hover:bg-white/10 h-12">
              <Link href="#features">View Demo</Link>
            </Button>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-gray-500 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-encore-success" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-encore-success" />
              <span>14-day free trial</span>
            </div>
          </div>
        </div>

        {/* 3D Dashboard Preview */}
        <div className="relative mx-auto max-w-5xl animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="relative rounded-xl bg-gray-900/50 border border-gray-800 p-2 shadow-2xl backdrop-blur-sm transform transition-transform duration-700 hover:scale-[1.01]">
            <div className="absolute inset-0 bg-gradient-to-tr from-encore-primary/10 to-transparent rounded-xl pointer-events-none" />
            
            {/* Window Controls */}
            <div className="h-8 flex items-center gap-2 px-4 border-b border-gray-800">
              <div className="w-3 h-3 rounded-full bg-red-500/20" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/20" />
              <div className="w-3 h-3 rounded-full bg-green-500/20" />
            </div>

            {/* Dashboard Placeholder/Mockup */}
            <div className="aspect-[16/9] bg-gray-950 rounded-b-lg overflow-hidden relative">
              {/* Mock Sidebar */}
              <div className="absolute left-0 top-0 bottom-0 w-64 border-r border-gray-800 bg-gray-900/50 hidden md:block">
                <div className="p-4 space-y-4">
                  <div className="h-8 w-32 bg-gray-800 rounded animate-pulse" />
                  <div className="space-y-2 pt-4">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="h-8 w-full bg-gray-800/50 rounded" />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Mock Content */}
              <div className="absolute left-0 md:left-64 top-0 right-0 bottom-0 p-6">
                <div className="flex justify-between mb-8">
                  <div className="h-8 w-48 bg-gray-800 rounded animate-pulse" />
                  <div className="h-8 w-24 bg-gray-800 rounded animate-pulse" />
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-32 bg-gray-800/30 border border-gray-800 rounded-lg p-4">
                      <div className="h-4 w-20 bg-gray-800 rounded mb-4" />
                      <div className="h-8 w-16 bg-gray-800/80 rounded" />
                    </div>
                  ))}
                </div>
                
                <div className="h-64 bg-gray-800/20 border border-gray-800 rounded-lg p-4 relative overflow-hidden">
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-encore-primary/20 to-transparent" />
                  {/* Mock Graph Lines */}
                  <div className="flex items-end justify-between h-full px-4 gap-2">
                    {[40, 60, 45, 70, 50, 80, 65, 85, 75, 90, 60, 95].map((h, i) => (
                      <div key={i} className="w-full bg-encore-primary/40 rounded-t" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Decorative Glow */}
          <div className="absolute -inset-4 bg-encore-primary/20 blur-3xl -z-10 rounded-[3rem]" />
        </div>
      </div>
    </section>
  )
}

